import classnames from 'classnames';
import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { AccordionClosedIcon, AccordionOpenIcon, IconProps } from '../../common/icons';

const styles = require('./accordion.css');

/** Props for the component */
export interface AccordionProps {
  /** Additional classes to apply */
  className?: string;
  /** Icon to show in the header */
  HeaderIcon: React.ComponentType<IconProps>;
  /** Header title */
  headerTitle: string;
  /** Header subtitle */
  headerSubtitle?: string;
  /** Additional classes to apply to the subtitle */
  headerSubtitleClass?: string;
  /** Free content to show in the header beneath the titles */
  headerContent?: JSX.Element;
  /** Content to show even when accordion is collapsed */
  collapsedContent?: JSX.Element;
  /** Whether to remove spacing around the collapsed content */
  noCollapsedContentPadding?: boolean;
  /** Content to show when accordion is expanded */
  expandedContent?: JSX.Element;
  /** Override expander label */
  expanderLabels?: [string, string];
}

/**
 * Expandable accordion showing information about the participant's HLC result.
 */
@observer
export class Accordion extends React.Component<AccordionProps> {
  /** Whether the accordion is in expanded mode */
  @observable.ref
  expanded: boolean = false;
  defaultExpanderLabels: [string, string] = ['Less information', 'More information'];

  /** Renders the component */
  render() {
    const expanderLabels = this.props.expanderLabels || this.defaultExpanderLabels;
    return (
      <div className={classnames(styles.container, this.props.className)}>
        <div className={styles.header} onClick={this.onToggleExpansion}>
          <this.props.HeaderIcon color="violet" />
          <div className={styles.heading}>
            <div className={styles.title}>{this.props.headerTitle}</div>
            <h4 className={this.props.headerSubtitleClass}>{this.props.headerSubtitle}</h4>
            <div>{this.props.headerContent}</div>
          </div>
          {this.props.expandedContent ? (
            <div className={styles.expander}>
              {this.expanded ? (
                <AccordionOpenIcon size="xsmall" color="violet" />
              ) : (
                <AccordionClosedIcon size="xsmall" color="violet" />
              )}
              {this.props.collapsedContent ? (
                // UX NOTE: When we have collapsed content, the expander is not
                // prominent enough in collapsed mode, so we show an additional
                // label when space allows.
                <span className={styles.expanderLabel}>{this.expanded ? expanderLabels[0] : expanderLabels[1]}</span>
              ) : (
                undefined
              )}
            </div>
          ) : (
            undefined
          )}
        </div>
        {this.props.collapsedContent ? (
          <div
            className={classnames(
              styles.collapsedContent,
              !this.props.noCollapsedContentPadding && styles.paddedContent
            )}
          >
            {this.props.collapsedContent}
          </div>
        ) : (
          undefined
        )}
        {this.expanded ? <div className={styles.expandedContent}>{this.props.expandedContent}</div> : undefined}
      </div>
    );
  }

  /** Expands or collapses the accordion */
  @action
  onToggleExpansion = () => {
    this.expanded = !this.expanded;
  };
}



// WEBPACK FOOTER //
// ./src/ui/widgets/accordion/accordion.tsx